/**
 * Shared Turnstile submit gate (AB#212).
 *
 * Cloudflare Turnstile resolves asynchronously, so a submit button that is live from first paint can
 * be clicked before a token exists. The POST then arrives with an empty cf-turnstile-response, the
 * server rejects it, and the user sees a failed sign-in with correct credentials and no explanation.
 * Tokens also expire (~5 minutes), which a login page left open reaches easily, so the same gap
 * reopens on a page that has been sitting there.
 *
 * This gates the submit button on having a token, and reacts to the states Turnstile actually has:
 * success, expiry and load/execute errors. The form fields stay enabled throughout - only submit
 * waits - so people can fill the form while the check runs and the wait is usually invisible.
 *
 * Client-side only. The server still validates the token; this never becomes the enforcement.
 *
 * Usage: put data-turnstile-gate on the widget container and point it at the submit button(s):
 *   <div id="turnstile-container"
 *        data-turnstile-gate
 *        data-sitekey="..."
 *        data-submit="#login-submit"      // selector, may match several buttons
 *        data-status="#turnstile-status"> // optional element for the message
 */
(function (window, document) {
    'use strict';

    // No "waiting" message. The first version showed "Checking your browser…" after a delay, which
    // read as a Cloudflare interstitial, sat away from the button it referred to, and appeared on the
    // email-gate step where nothing was actually blocked. The disabled button carries the wait; text
    // is reserved for the states the user cannot otherwise explain - expiry and load failure.
    var gates = [];

    // AB#458: Cloudflare's 600xxx family is "challenge execution failure", and their guidance is to
    // reset and try again because it is usually transient. Before this, one error was terminal: the
    // submit button stayed disabled and the only advice was to refresh, which does nothing when the
    // cause is persistent. Two retries, backing off, then an honest message.
    var RETRY_DELAYS_MS = [1200, 3000];

    function text(el, message) {
        if (el) el.textContent = message || '';
    }

    function setSubmitEnabled(gate, enabled) {
        for (var i = 0; i < gate.buttons.length; i++) {
            gate.buttons[i].disabled = !enabled;
        }
    }

    function messages(gate) {
        // Views can override these via data-* so copy stays translatable in the view.
        var el = gate.container;
        return {
            expired: el.getAttribute('data-msg-expired') || 'Verification expired, checking again…',
            retrying: el.getAttribute('data-msg-retrying') || 'Verification did not load. Trying again…',
            // Deliberately not "refresh the page". The common cause is the browser blocking the
            // storage Turnstile needs - tracking prevention, third-party cookie blocking, an in-app
            // browser - and reloading cannot fix any of those. Naming the cause is the difference
            // between a parent trying something that works and a parent giving up.
            blocked: el.getAttribute('data-msg-blocked')
                || 'The security check could not run. Your browser may be blocking it. Try another browser, or allow this site in your privacy settings.',
            error: el.getAttribute('data-msg-error') || 'Verification could not load. Refresh the page to try again.'
        };
    }

    function onSuccess(gate) {
        // A later expiry or blip gets its own retries rather than inheriting an exhausted count.
        gate.errorCount = 0;
        text(gate.status, '');
        setSubmitEnabled(gate, true);
    }

    function onExpired(gate) {
        setSubmitEnabled(gate, false);
        text(gate.status, messages(gate).expired);
        gate.reset();
    }

    function onError(gate) {
        setSubmitEnabled(gate, false);

        if (gate.errorCount < RETRY_DELAYS_MS.length) {
            var delay = RETRY_DELAYS_MS[gate.errorCount];
            gate.errorCount++;
            text(gate.status, messages(gate).retrying);
            window.setTimeout(function () { gate.reset(); }, delay);
            return;
        }

        text(gate.status, messages(gate).blocked);
    }

    function render(gate) {
        if (!window.turnstile || !gate.container) return;
        // Guard against double render (explicit onload + bfcache restore).
        if (gate.container.querySelector('iframe')) return;

        gate.widgetId = window.turnstile.render(gate.container, {
            sitekey: gate.container.getAttribute('data-sitekey'),
            theme: gate.container.getAttribute('data-theme') || 'light',
            size: gate.container.getAttribute('data-size') || 'flexible',
            // AB#230: fixed, not a per-view knob. Since this gate holds submit until a token arrives,
            // an invisible widget turns a stalled challenge into a dead button with nothing on screen
            // to act on. The widget always renders so people can see whether it wants a click or has
            // already passed.
            appearance: 'always',
            callback: function () { onSuccess(gate); },
            'expired-callback': function () { onExpired(gate); },
            'error-callback': function () { onError(gate); }
        });
    }

    function initGate(container) {
        var submitSelector = container.getAttribute('data-submit');
        var statusSelector = container.getAttribute('data-status');

        var gate = {
            container: container,
            buttons: submitSelector ? [].slice.call(document.querySelectorAll(submitSelector)) : [],
            status: statusSelector ? document.querySelector(statusSelector) : null,
            widgetId: null,
            errorCount: 0
        };

        // How a retry actually happens for a widget this file rendered itself.
        gate.reset = function () {
            if (window.turnstile && gate.widgetId !== null) {
                window.turnstile.reset(gate.widgetId);
            }
        };

        // Hold submit until a token arrives. Fields stay enabled, and nothing is announced - the
        // disabled button is the signal.
        setSubmitEnabled(gate, false);

        gates.push(gate);
        render(gate);
        return gate;
    }

    function initAll() {
        var containers = document.querySelectorAll('[data-turnstile-gate]');
        for (var i = 0; i < containers.length; i++) {
            initGate(containers[i]);
        }
    }

    // Turnstile calls this once its script loads (api.js?onload=onTurnstileGateLoad).
    window.onTurnstileGateLoad = function () {
        if (gates.length === 0) {
            initAll();
            return;
        }
        for (var i = 0; i < gates.length; i++) render(gates[i]);
    };

    // Back/forward cache restores a page with a stale widget: re-render so the token is fresh.
    window.addEventListener('pageshow', function (e) {
        if (!e.persisted) return;
        for (var i = 0; i < gates.length; i++) {
            var gate = gates[i];
            setSubmitEnabled(gate, false);
            gate.container.innerHTML = '';
            gate.widgetId = null;
            render(gate);
        }
    });

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAll);
    } else {
        initAll();
    }

    /**
     * For pages that render Turnstile themselves and only want the submit-gating behaviour.
     * The account page copies one widget's token into hidden inputs across several forms, so it keeps
     * its own render and token plumbing and calls ready/expired/error from its existing callbacks.
     * Returns the same three transitions this file uses internally, so the UX rules live in one place.
     *
     * AB#458: pass `container` so the view's data-msg-* copy is found - without it messages fall back
     * to the built-in English. Pass `reset` so a retry can reach the widget the page rendered; this
     * file does not own its id.
     */
    function attach(options) {
        var opts = options || {};
        var container = opts.container
            ? (typeof opts.container === 'string' ? document.querySelector(opts.container) : opts.container)
            : null;

        var gate = {
            container: container || document.createElement('div'),
            buttons: opts.submit ? [].slice.call(document.querySelectorAll(opts.submit)) : [],
            status: opts.status ? document.querySelector(opts.status) : null,
            widgetId: null,
            errorCount: 0
        };

        gate.reset = typeof opts.reset === 'function' ? opts.reset : function () { };

        setSubmitEnabled(gate, false);

        return {
            ready: function () { onSuccess(gate); },
            expired: function () {
                setSubmitEnabled(gate, false);
                text(gate.status, messages(gate).expired);
            },
            error: function () { onError(gate); }
        };
    }

    window.TurnstileGate = { initAll: initAll, attach: attach, gates: gates };
})(window, document);
